import {useEffect, useMemo, useState} from 'react';
import {TimeSheetItem, TimeSheetItemStatus, TimeSheetItemType} from '../../../features/schedule/types';
import {useAppDispatch, useAppSelector} from '../../../hooks/hooks';
import {getShifts} from '../../../features/shifts';
import {getAbsences} from '../../../features/absences';
import {AutoCompleteItemValue} from '../../../components/shared/nextgen/selector/types';
import {setTimesheetData} from '../../../features/schedule';
import {absenceAsTimeSheetItem, containsStatus, createEmptyTimesheetItem, shiftAsTimeSheetItem} from '../functions';
import {canSeeOtherTimesheetItems} from '../../../features/profile/entityPermissions';

import _ from 'lodash';

const isAbortError = (error: unknown): boolean => {
    if (!error || typeof error !== 'object') {
        return false;
    }

    const candidate = error as { name?: string; code?: string; message?: string };

    return candidate.name === 'AbortError'
        || candidate.name === 'CanceledError'
        || candidate.code === 'ERR_CANCELED'
        || candidate.message === 'Aborted';
};

const getUpdatedTimeBankHours = (
    item: TimeSheetItem,
    previousItem?: TimeSheetItem
): number | undefined => {
    if (item.timeBankHours !== undefined) {
        return item.timeBankHours;
    }

    if (previousItem?.timeBankHours === undefined) {
        return undefined;
    }

    if (item.hours === previousItem.hours) {
        return previousItem.timeBankHours;
    }

    const hours = Math.abs(item.hours ?? 0);
    return item.timeshiftType === TimeSheetItemType.ABSENCE
        ? -hours
        : hours;
};

const useTimeSheetView = () =>
{
    const dispatch = useAppDispatch();
    const [initial, setInitial] = useState<boolean>(true)
    const [loading, setLoading] = useState<boolean>(true);
    const employees = useAppSelector((selector) => selector.rEmployees);
    const projects = useAppSelector((selector) => selector.eProjects);
    const profile = useAppSelector((selector) => selector.profile);
    const filter = useAppSelector((selector) => selector.timeTrackingFilters?.filter);
    const data = useAppSelector((selector) => selector.timesheet?.data ?? []);
    const canViewOtherTimesheetItems = canSeeOtherTimesheetItems(profile);
    const ownEmployeeId = profile.id;
    const ownEmployeeOptions = useMemo(() => (
        ownEmployeeId
            ? [{ id: ownEmployeeId, name: profile.name, isDefault: false }]
            : []
    ), [ownEmployeeId, profile.name]);
    const employeeOptions = useMemo(() => {
        if (canViewOtherTimesheetItems && employees.length > 0) {
            return employees;
        }

        return ownEmployeeOptions.length > 0 ? ownEmployeeOptions : employees;
    }, [canViewOtherTimesheetItems, employees, ownEmployeeOptions]);
    
    const selectedEmployees = useMemo(() => {
        if (canViewOtherTimesheetItems) {
            return filter.propertyFilter.selectedEmployees;
        }

        return ownEmployeeId
            ? [{ id: ownEmployeeId, name: profile.name, originalId: ownEmployeeId }]
            : [];
    }, [canViewOtherTimesheetItems, filter.propertyFilter.selectedEmployees, ownEmployeeId, profile.name]);

    const load = async (
        fromDate: string,
        toDate: string,
        employee: AutoCompleteItemValue[],
        projects: AutoCompleteItemValue[],
        shiftTypes?: AutoCompleteItemValue[],
        absenceTypes?: AutoCompleteItemValue[],
        requests: Array<{ abort: () => void }> = []
    ): Promise<Array<TimeSheetItem>> => {
        const promises: Array<Promise<TimeSheetItem[]>> = [];
        if (!((absenceTypes && absenceTypes.length > 0) && (shiftTypes && shiftTypes.length < 1) && projects.length < 1)) {
            const shiftsRequest = dispatch(getShifts({
                dateFrom: fromDate,
                dateTo: toDate,
                employees: employee?.map((x) => (x.originalId as number)) ?? [],
                projects: projects?.map((x) => (x.originalId as number)) ?? [],
                types: shiftTypes?.map((x) => (x.originalId as number)) ?? []
            }));
            requests.push(shiftsRequest);
            promises.push(shiftsRequest.unwrap().then((r) => r.map((s) => shiftAsTimeSheetItem(s, employeeOptions))));
        }
        if (!((projects.length > 0 || (shiftTypes && shiftTypes.length > 0)) && (absenceTypes && absenceTypes.length < 1))) {
            const absencesRequest = dispatch(getAbsences({
                dateFrom: fromDate,
                dateTo: toDate,
                employees: employee?.map((x) => (x.originalId as number)) ?? [],
                types: absenceTypes?.map((x) => (x.originalId as number)) ?? []
            }));
            requests.push(absencesRequest);
            promises.push(absencesRequest.unwrap().then((r) => r
                .map((a) => absenceAsTimeSheetItem(a, employeeOptions))
                .filter((item): item is TimeSheetItem => item !== null)
            ));
        }

        return (await Promise.all(promises)).flat(1);
    }

    const updateItems = (items: TimeSheetItem[]) => {
        const current = [...data];
        items.forEach((it) => {
            const srcIndex = current.findIndex((x) => x.id == it.id && x.timeshiftType == it.timeshiftType);
            current[srcIndex] = it;
        })

        proceedItems(current).then();
    }

    const updateItem = (item: TimeSheetItem) => {
        const current = [...data];

        const srcIndex = current.findIndex((x) => x.id === item.id && x.timeshiftType === item.timeshiftType);
        const previousItem = current[srcIndex];
        current[srcIndex] = {
            ...previousItem,
            ...item,
            date: item.date,
            note: item.note,
            itemId: item.itemId,
            hours: item.hours,
            timeBankHours: getUpdatedTimeBankHours(item, previousItem)
        };

        proceedItems(current).then();
    }

    const addItem = (item: TimeSheetItem) => {
        proceedItems([...data, item]).then();
    }

    const removeItem = (item: TimeSheetItem) => {
        const current = _.chain(data);
        const filtered = current.filter((x) => !(x.id === item.id && x.timeshiftType === item.timeshiftType));
        proceedItems(filtered.value()).then();
    }

    const afterLoad = (items: TimeSheetItem[]) => {
        const noEmployeeData = employeeOptions.filter((x) => items.filter((y) => x.id === y.employeeId).length < 1)
            .map((x) => createEmptyTimesheetItem(x));
        const noProjectData = projects.filter((x) => items.filter((y) => x.id === y.targetId).length < 1)
            .map((x) => createEmptyTimesheetItem(undefined, x));

        proceedItems([...items, ...noEmployeeData, ...noProjectData]).then();
    }

    const proceedItems = async (items: TimeSheetItem[]) => {
        const statusFilter = filter.propertyFilter.selectedStatuses ?? [];

        const startDate = new Date(filter.dateRangeFilter.startDate).getTime();
        const endDate = new Date(filter.dateRangeFilter.endDate).getTime();

        setTimesheetData([...items]
            .filter((x) => {
                if (x.date === '') {
                    return true;
                }
                const date = new Date(x.date).getTime();
                return date >= startDate && date <= endDate;
            })
            .filter((x) =>
                (selectedEmployees.length > 0 ? selectedEmployees.findIndex((y) => y.originalId === x.employeeId) !== -1 : true)
                && (filter.propertyFilter.selectedProjects.length > 0 ? x.timeshiftType === TimeSheetItemType.SHIFT && filter.propertyFilter.selectedProjects.findIndex((y) => y.originalId === x.targetId) !== -1 : true)
                && (filter.propertyFilter.selectedProjectTypes && filter.propertyFilter.selectedProjectTypes.length > 0 ? x.timeshiftType === TimeSheetItemType.SHIFT && filter.propertyFilter.selectedProjectTypes.findIndex((y) => y.originalId === x.targetProjectTypeId) !== -1 : true)
                && (filter.propertyFilter.selectedShiftTypes && filter.propertyFilter.selectedShiftTypes.length > 0 ? x.timeshiftType === TimeSheetItemType.SHIFT && filter.propertyFilter.selectedShiftTypes.findIndex((y) => y.originalId === x.timeshiftTypeId) !== -1 : true)
                && (filter.propertyFilter.selectedAbsenceTypes && filter.propertyFilter.selectedAbsenceTypes.length > 0 ? x.timeshiftType === TimeSheetItemType.ABSENCE && filter.propertyFilter.selectedAbsenceTypes.findIndex((y) => y.originalId === x.timeshiftTypeId) !== -1 : true))
            .filter((x) =>
                statusFilter.length > 0 ? (x.status === TimeSheetItemStatus.NORMAL && containsStatus(statusFilter, TimeSheetItemStatus.NORMAL))
                    || (containsStatus(statusFilter, TimeSheetItemStatus.APPROVED) && (x.status === TimeSheetItemStatus.APPROVED
                        || x.status === TimeSheetItemStatus.BILLABLE))
                    || (x.status === TimeSheetItemStatus.BILLABLE && containsStatus(statusFilter, TimeSheetItemStatus.BILLABLE)) : true))
    }

    useEffect(() => {
        if (filter.dateRangeFilter.startDate === '' || filter.dateRangeFilter.endDate === '' || employeeOptions.length < 1) {
            setLoading(false);
            return;
        }

        let active = true;
        const requests: Array<{ abort: () => void }> = [];
        setLoading(true);
        if (initial) {
            setInitial(false);
        }

        load(
            filter.dateRangeFilter.startDate,
            filter.dateRangeFilter.endDate,
            selectedEmployees,
            filter.propertyFilter.selectedProjects,
            filter.propertyFilter.selectedShiftTypes,
            filter.propertyFilter.selectedAbsenceTypes,
            requests
        )
            .then((r) => {
                if (!active) {
                    return;
                }

                afterLoad(r);
            })
            .catch((error) => {
                if (!isAbortError(error)) {
                    console.error('Timesheet load failed', error);
                }
            })
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });

        return () => {
            active = false;
            requests.forEach((request) => request.abort());
        };
    }, [employeeOptions, filter, selectedEmployees]);

    return { data: data, loading, addItem, removeItem, updateItems, updateItem };
}

export default useTimeSheetView;
